import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Motion";
import { api } from "@/lib/api";
import { visitorId } from "@/lib/track";

const logAd = (adId, kind) => {
  if (adId) api.post(`/ads/${adId}/event`, { kind, vid: visitorId() }).catch(() => {});
};

const ADS = [
  {
    id: "designers", img: "/img/designer.jpg", alt: "Fashion designer sketching in her studio",
    kicker: "Designers", title: "Designers looking for collaborations",
    text: "Independent labels and fashion designers are searching for venues, runway slots, brands and retailers to collaborate with. See who's looking right now.",
    cta: "See designer opportunities", to: "/discover?tab=opportunities&q=fashion",
  },
  {
    id: "influencers", img: "/img/influencer.jpg", alt: "Influencer filming content on a phone",
    kicker: "Influencers", title: "Influencers ready to collaborate or get sponsored",
    text: "Creators and influencers are open to brand deals, sponsorships and co-created content. Browse their profiles and send a partnership request.",
    cta: "Browse influencers", to: "/discover?type=Creator",
  },
  {
    id: "sports", img: "/img/stadium.jpg", alt: "Packed football stadium",
    kicker: "Sports", title: "Sports teams looking for sponsors",
    text: "Clubs, leagues and athletes offer shirt, stadium and digital sponsorships. Find the right fit for your brand.",
    cta: "See sports teams", to: "/discover?type=Sports%20Team",
  },
  {
    id: "venues", img: "/img/gala.jpg", alt: "Set tables at an event venue",
    kicker: "Venues", title: "Venues ready to host your next event",
    text: "Hotels, galleries and event spaces open to brand activations, launches and co-hosted events.",
    cta: "Browse venues", to: "/discover?type=Venue",
  },
  {
    id: "nonprofits", img: "/img/handshake.jpg", alt: "Two people shaking hands",
    kicker: "Nonprofits", title: "Nonprofits seeking corporate partners",
    text: "Charities and foundations looking for sponsors, cause partnerships and employee-giving programmes.",
    cta: "See nonprofits", to: "/discover?type=Nonprofit",
  },
  {
    id: "events", img: "/img/neon.jpg", alt: "Crowd at a live event",
    kicker: "Events", title: "Events looking for sponsors worldwide",
    text: "Conferences, festivals and summits in every country with sponsorship packages ready to book.",
    cta: "See events", to: "/events",
  },
];

const rotation = () => {
  const start = Math.floor(Date.now() / 86400000) % ADS.length;
  return [ADS[start], ADS[(start + 1) % ADS.length]];
};

const CtaLink = ({ a }) => /^https?:\/\//.test(a.to)
  ? <a href={a.to} target="_blank" rel="noopener noreferrer sponsored" onClick={() => logAd(a.adId, "click")} className="btn-gold mt-6" data-testid={`ad-${a.id}-cta`}>{a.cta} <ArrowRight className="h-4 w-4" /></a>
  : <Link to={a.to} onClick={() => logAd(a.adId, "click")} className="btn-gold mt-6" data-testid={`ad-${a.id}-cta`}>{a.cta} <ArrowRight className="h-4 w-4" /></Link>;

const fromBooking = (b) => ({
  id: `booked-${b.id}`, adId: b.id, img: b.image_url ? `${process.env.REACT_APP_BACKEND_URL}${b.image_url}` : "/img/summit.jpg",
  alt: b.title, kicker: b.company_name, title: b.title, text: b.text, cta: b.cta_label, to: b.link,
});

function useViewLog(adId) {
  const ref = useRef(null);
  useEffect(() => {
    if (!adId || !ref.current || !("IntersectionObserver" in window)) return undefined;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { logAd(adId, "view"); io.disconnect(); }
    }, { threshold: 0.5 });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [adId]);
  return ref;
}

const AdCard = ({ a, i }) => {
  const ref = useViewLog(a.adId);
  return (
  <Reveal delay={i * 0.08}>
  <div ref={ref} className="group relative h-full overflow-hidden rounded-2xl border border-[#2A1240]/10 bg-white" data-testid={`ad-${a.id}`}>
    <div className="relative aspect-[16/9] overflow-hidden">
      <img src={a.img} alt={a.alt} className="absolute inset-0 h-full w-full object-cover" />
      <span className="absolute left-4 top-4 rounded-md bg-white/90 px-2.5 py-1 text-xs font-semibold text-[#2A1240]">Featured</span>
    </div>
    <div className="p-6 sm:p-8">
      <p className="kicker">{a.kicker}</p>
      <h3 className="mt-2 font-display text-2xl sm:text-3xl text-[#2A1240] leading-tight">{a.title}</h3>
      <p className="mt-3 text-sm sm:text-base text-slate-300">{a.text}</p>
      <CtaLink a={a} />
    </div>
  </div>
  </Reveal>
  );
};

export const CollabAds = () => {
  const [booked, setBooked] = useState([]);
  useEffect(() => {
    api.get("/ads/live").then((r) => setBooked(Array.isArray(r.data) ? r.data.slice(0, 2).map(fromBooking) : [])).catch(() => {});
  }, []);
  const ads = [...booked, ...rotation()].slice(0, 2);
  return (
    <section className="relative py-16 sm:py-20" data-testid="collab-ads">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid gap-6 md:grid-cols-2">
        {ads.map((a, i) => <AdCard key={a.id} a={a} i={i} />)}
      </div>
      <p className="mx-auto max-w-7xl px-5 sm:px-8 mt-4 text-sm text-slate-400">Want your business here? <Link to="/app/promote" className="text-[#8E2BC4] underline underline-offset-4" data-testid="ad-book-link">Book a Featured spot</Link></p>
    </section>
  );
};
